import { RecordDialog, RecordFabButton } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { EntityFilters } from '@tunasong/models'
import { getAcceptedMimeTypes, usePlugins } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import { isFolderContainer } from '@tunasong/schemas'
import { useSearch } from '@tunasong/search'
import { ContentContainer, DropZone, Typography, useDimensions } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { useState } from 'react'
import { CollaboratorsSection } from './collaborators-section.js'
import { ContinueSection } from './continue-section.js'
import { HomeHeader } from './home-header.js'
import { SpacesSection } from './spaces-section.js'
import { UnfiledSection } from './unfiled-section.js'
import { useContinueItems } from './use-continue-items.js'
import { useHomeCreate } from './use-home-create.js'
import { WaitingOnYouSection } from './waiting-on-you-section.js'
import styles from './root-main.module.css'

export interface RootMainProps {}

/**
 * Home (`/app`). One create action in the header, Waiting on you when access requests are pending (request-access
 * spec §8.5), Continue (pins folded in) full width, then a wrapping band:
 * Spaces beside an aside of collaborator activity and Unfiled. The whole page is the upload drop target;
 * dropped files land at the root, which Home lists as Unfiled.
 *
 * @see docs/ux/2026-10-16-home-dashboard-redesign-ux.md
 */
export const RootMain: FC<RootMainProps> = props => {
  const {} = props
  const { userId } = useCurrentUser()
  const { isTiny } = useDimensions()
  const { createFromTemplate, createSpace, busy } = useHomeCreate()
  const [recording, setRecording] = useState(false)
  // First run: the empty Continue box carries its own drop hint, so the page footer does not repeat it
  const { isEmpty: isFirstRun } = useContinueItems()

  const { results: sharedSearchResults, searching } = useSearch({ queryType: 'shared', query: userId, size: 500 })
  const { entities: ownedEntities, isLoading: isLoadingEntities } = graphHooks.useEntities({
    root: true,
    filter: EntityFilters.folder,
  })

  // Shared results whose parent is also shared are reached through that parent, and owned ones are listed already
  const sharedResultIds = new Set(sharedSearchResults.map(r => r.id))
  const ownedResultIds = new Set(ownedEntities.map(r => r.id))
  const sharedResults = sharedSearchResults
    .filter(r => (r.parentId ? !sharedResultIds.has(r.parentId) : true))
    .filter(r => !ownedResultIds.has(r.id))

  const spaces = [...ownedEntities, ...sharedResults]
    .filter(e => !e.parentId || e.parentId === 'ROOT')
    .filter(isFolderContainer)
  const spaceIds = new Set(spaces.map(s => s.id))
  const unfiled = ownedEntities.filter(e => !spaceIds.has(e.id))

  const plugins = usePlugins('all')
  const mimeTypes = getAcceptedMimeTypes(plugins)
  const accept = [...Object.keys(mimeTypes), ...Object.values(mimeTypes).flat()].join(',')

  const handleRecord = () => setRecording(true)

  return (
    <ContentContainer className={isTiny ? styles.scrollerWithFab : undefined}>
      <DropZone
        parentId={null}
        accept={mimeTypes}
        keepChildrenOnDrag
        className={cn(styles.page, { [styles.pageWithFab]: isTiny })}
      >
        <HomeHeader
          showRecord={!isTiny}
          busy={busy}
          accept={accept}
          onNewSong={() => createFromTemplate('song', 'New Song')}
          onNewSetlist={() => createFromTemplate('setlist', 'New Setlist')}
          onNewPage={() => createFromTemplate('page', 'New Page')}
          onNewSpace={() => createSpace()}
          onRecord={handleRecord}
        />
        {/* Renders nothing unless someone is waiting on me (request-access spec §8.5) */}
        <WaitingOnYouSection />
        <ContinueSection
          busy={busy}
          onNewSong={() => createFromTemplate('song', 'New Song')}
          onNewBand={() => createFromTemplate('bandspace', 'New Band')}
          onNewProject={() => createSpace('New Project')}
          onRecord={handleRecord}
        />
        <div className={styles.band}>
          <div className={styles.main}>
            <SpacesSection
              spaces={spaces}
              loading={isLoadingEntities || searching}
              busy={busy}
              onNewSpace={() => createSpace()}
            />
          </div>
          {/* A plain container, not <aside>: a complementary landmark must not sit inside the app's <main>. It
           * collapses (:empty) when both sections hide, so Spaces takes the full width. */}
          <div className={styles.aside}>
            <CollaboratorsSection />
            <UnfiledSection entities={unfiled} />
          </div>
        </div>
        {isFirstRun ? null : (
          <Typography variant="caption" component="p" className={styles.dropHint}>
            Drop audio, images or PDFs anywhere on this page. They land in Unfiled.
          </Typography>
        )}
      </DropZone>
      {isTiny ? (
        <div className={styles.fab}>
          <RecordFabButton position="relative" color="primary" />
        </div>
      ) : null}
      <RecordDialog open={recording} onClose={() => setRecording(false)} />
    </ContentContainer>
  )
}

export default RootMain
